<template>
  <div 
    ref="containerRef"
    class="multiselect-container relative w-full group/multiselect"
    :class="
      [{ 'h-full': fillHeight }, { 'borderless': borderless }, { 'invisible-input': invisibleInput }, { 'no-label': !inputLabel }]
    "
    :style="{ 
      '--ai-multiselect-bg': bgToCssVar,
      '--ai-multiselect-border': colorNameToCssVar(internalBorderColor),
      '--ai-multiselect-border-focus': colorNameToCssVar(internalBorderColorFocus),
      '--ai-multiselect-border-hover': colorNameToCssVar(internalBorderColorHover),
      '--ai-multiselect-label-focus': colorNameToCssVar(internalLabelColorFocus)
    }"
  >
    <div
      v-if="required && !requiredIconHidden"
      v-tooltip="'Required field'"
      class="absolute right-0 pt-0.5 pr-1.5 text-lg text-error z-40"
    >
      *
    </div>

    <label
      v-if="inputLabel"
      class="ai-multiselect_input-label"
      :class="[
        {
          '!px-1 !ml-2 transform -translate-y-4 !scale-75 !top-1 left-0 origin-[0] opacity-100':
            value && value.length !== 0,
          'opacity-0 invisible': !value || value.length === 0,
          'group-focus-within/multiselect:opacity-100 group-focus-within/multiselect:visible': true,
        },
        value && value.length !== 0 ? internalBgColor : ''
      ]"
      :style="labelStyles"
      >{{ inputLabel }}</label
    >
    <!-- No hidden input - using programmatic validation only -->
    
    <multiselect
      v-model="value"
      :options="options"
      :placeholder="placeholder"
      :label="isObjectOptions ? label : ''"
      :track-by="isObjectOptions ? trackBy : ''"
      :showLabels="false"
      :multiple="multiple"
      :close-on-select="!multiple"
      :class="[
        inputClass, { 'ai-multiselect-input-error': error || showValidationError },
       { 'h-full': fillHeight },
      ]"
      :open-direction="openDirection"
      @select="onSelect"
      @update:modelValue="updateModelValue"
      @remove="onRemove"
    ></multiselect>
  </div>
</template>

<script setup>
import { ref, computed, watch, inject } from "vue"
import Multiselect from "vue-multiselect"
import { colorNameToBgClass, colorNameToCssVar } from "@/composables/useColors"

const formSettings = inject('form') ?? {}

const props = defineProps({
  fillHeight: {
    type: Boolean,
    default: false,
  },
  borderless: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: [Array, Object, String, Number],
  },
  trackBy: {
    type: String,
    default: "name",
  },
  label: {
    type: String,
  },
  placeholder: {
    type: String,
    default: " ",
  },
  options: {
    type: Array,
    required: true,
  },
  inputLabel: {
    type: String
  },
  inputClass: {
    type: [String, Array],
    default: [],
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  requiredIconHidden: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  selectionReturnOnly: {
    type: String,
    default: null,
  },
  openDirection: {
    type: String,
    default: "bottom",
  },
  error: {
    type: Boolean,
    default: false,
  },
  invisibleInput: {
    type: Boolean,
    default: false,
  },
  bgColor: {
    type: String,
    default: '',
  },
  borderColor: {
    type: String,
    default: '',
  },
  borderColorFocus: {
    type: String,
    default: '',
  },
  borderColorHover: {
    type: String,
    default: '',
  },
  labelColorFocus: {
    type: String,
    default: '',
  },
})


const internalBgColor = computed(() => {
  const colorName = props.bgColor || formSettings.bgColor || 'base-100'
  return colorNameToBgClass(colorName)
})

const internalBorderColor = computed(() => props.borderColor || formSettings.borderColor || 'form-border')
const internalBorderColorFocus = computed(() => props.borderColorFocus || formSettings.borderColorFocus || 'form-border-focus')
const internalBorderColorHover = computed(() => props.borderColorHover || formSettings.borderColorHover || 'form-border-hover')
const internalLabelColorFocus = computed(() => props.labelColorFocus || formSettings.labelColorFocus || 'base-content')

// Create inline styles for focus-within and active states since dynamic Tailwind classes get purged
const labelStyles = computed(() => ({
  '--label-focus-bg': colorNameToCssVar(props.bgColor || formSettings.bgColor || 'base-100'), 
  '--label-active-bg': colorNameToCssVar(props.bgColor || formSettings.bgColor || 'base-100')
}))

// Map background color name to CSS custom properties
const bgToCssVar = computed(() => {
  const colorName = props.bgColor || formSettings.bgColor || 'base-100'
  return colorNameToCssVar(colorName)
})

const value = ref(props.modelValue == null ? [] : props.modelValue)
const showValidationError = ref(false)
const containerRef = ref(null)

const emit = defineEmits(["input", "update:modelValue", "validation-failed"])

const isObjectOptions = computed(() => {
  return props.options?.length > 0 && typeof props.options[0] === "object"
})

// Computed value for browser validation - needs to be non-empty when a selection is made
const validationValue = computed(() => {
  if (!value.value) return ''
  if (Array.isArray(value.value)) {
    return value.value.length > 0 ? 'selected' : ''
  }
  return value.value ? 'selected' : ''
})

const updateModelValue = value => {
  let transformedValue = value
  if (props.selectionReturnOnly && isObjectOptions.value) {
    transformedValue = value.map(item => item[props.selectionReturnOnly])
  }
  emit("update:modelValue", transformedValue)
  // Clear validation error when user selects something
  showValidationError.value = false
}
const onRemove = value => {
  //console.log("onremove", value)
}

const onSelect = value => {
  //console.log("onselect", value)
}

// Method for parent to call to focus this multiselect
const focusAndOpen = () => {
  // Show validation error styling
  showValidationError.value = true
  
  setTimeout(() => {
    console.log("focusAndOpen called")
    if (containerRef.value) {
      // Find the dropdown arrow/select button first (most reliable)
      const selectButton = containerRef.value.querySelector('.multiselect__select')
      const tagsElement = containerRef.value.querySelector('.multiselect__tags')
      const input = containerRef.value.querySelector('.multiselect__input')
      
      console.log('focusAndOpen called', { selectButton, tagsElement, input })
      
      // Scroll into view first
      containerRef.value.scrollIntoView({ behavior: 'smooth', block: 'center' })
      
      // Try multiple strategies to open the dropdown
      setTimeout(() => {
        // Strategy 1: Click the select button (dropdown arrow)
        if (selectButton) {
          console.log('Clicking select button')
          selectButton.click()
        }
        
        // Strategy 2: Click the tags area
        if (tagsElement) {
          console.log('Clicking tags element')
          tagsElement.click()
        }
        
        // Strategy 3: Focus the input
        if (input) {
          console.log('Focusing input')
          input.focus()
        }
      }, 200)
    } else {
      console.log('containerRef.value is null')
    }
  }, 0)
}

watch(
  () => props.modelValue,
  (newModelValue) => {
    value.value = newModelValue
    // Clear validation error if value is now valid
    if (newModelValue && (Array.isArray(newModelValue) ? newModelValue.length > 0 : true)) {
      showValidationError.value = false
    }
  },
)

// Expose methods for parent component
defineExpose({ focusAndOpen })
</script>

<style>
.ai-multiselect-input-error {
  --ai-multiselect-border: oklch(var(--er)) !important;
  --ai-multiselect-border-focus: oklch(var(--er)) !important;
  --ai-multiselect-border-hover: oklch(var(--er)) !important;
}
.ai-multiselect_input-label {
  @apply absolute 
    text-sm 
    text-base-content
    dark:text-white
    z-20 
    top-2
    scale-100
    rounded-full
    mx-2.5
    leading-6
    origin-left /* Added to anchor transformations to the left */
    group-focus-within/multiselect:scale-75
    group-focus-within/multiselect:z-[999]
    group-focus-within/multiselect:duration-300
    group-focus-within/multiselect:transform
    group-focus-within/multiselect:-translate-y-4
    group-focus-within/multiselect:top-1
    group-focus-within/multiselect:origin-[0]
    group-focus-within/multiselect:px-1
    group-focus-within/multiselect:ml-2
    group-focus-within/multiselect:opacity-100
    opacity-100
    group-focus-within/multiselect:left-0
    group-focus-within/multiselect:visible
    pointer-events-none;
}

.ai-multiselect_input-label {
  background-color: var(--label-focus-bg, transparent);
}

.group\/multiselect:focus-within .ai-multiselect_input-label {
  background-color: var(--label-focus-bg);
  color: var(--ai-multiselect-label-focus) !important;
  font-weight: normal;
}
.multiselect-container :disabled {
  opacity: 0.7;
}

/* Color Borders for active state*/

.multiselect__tags {
  border: 1px solid var(--ai-multiselect-border);
  transition: border-color 200ms;
}

.multiselect-container.borderless .multiselect__tags{
  @apply border-none;
}
.multiselect-container.borderless .multiselect__content-wrapper{
  @apply rounded-md;
  border: 1px solid var(--ai-multiselect-border);
}


.multiselect--active .multiselect__content-wrapper,
.multiselect--active .multiselect__tags {
  border-color: var(--ai-multiselect-border-focus);
}

.multiselect--active .multiselect__content-wrapper{
  background-color: var(--ai-multiselect-bg, oklch(var(--b1)));
}

.multiselect__tags:hover:not(.multiselect--active .multiselect__tags) {
  border-color: var(--ai-multiselect-border-hover);
}

.multiselect__tags{
  @apply h-full;
}

.multiselect__input,
.multiselect__single {
  background-color: var(--ai-multiselect-bg, oklch(var(--b1)));
  @apply text-sm text-base-content px-0 !h-0;
  letter-spacing: inherit;
}

.invisible-input .multiselect__input {
  position: fixed !important;
  z-index: -100;
  opacity: 0;
  margin-top: -10rem;
  line-height: 0rem !important;
  padding: 0px;
  margin: 0px;
}

.multiselect__input::placeholder {
  @apply text-black/40 dark:text-white/40;
}

.multiselect__input:focus::placeholder {
  @apply text-black/20 dark:text-white/20;
}

.multiselect__single {
  padding-top: 3px;
}

/* Remove top padding when no label is provided */
.no-label .multiselect__single {
  padding-top: 0;
}
.multiselect__tag {
  @apply !bg-zinc-300 !text-base-content/90 dark:!text-primary-content dark:!bg-primary;
}
.multiselect__tag-icon:after {
  @apply !text-primary-content/50;
}
.multiselect__tag-icon:hover {
  @apply !bg-[rgba(0,0,0,0.15)];
}

.multiselect__tags {
  background-color: var(--ai-multiselect-bg, oklch(var(--b1)));
  @apply outline-none border text-sm  rounded-md;
  padding: 0.625rem 40px 0 0.625rem;
  line-height: 1rem !important;
}

.multiselect--active .multiselect__tags {
  @apply border-b-0;
}

.multiselect__placeholder {
  @apply text-sm text-base-content/80;
  line-height: 1rem !important;
}
.multiselect__option {
  @apply text-sm text-base-content/70;
}
.multiselect__option--highlight {
  @apply !bg-primary/20  text-base-content/80;
}
.multiselect__option--selected {
  @apply !bg-primary/20  text-base-content/70 font-normal;
}
.multiselect__option--selected.multiselect__option--highlight{
  @apply text-base-content/60;
}

.multiselect__element .multiselect__option--selected:hover,
.multiselect__element .multiselect__option--selected:hover:after {
  @apply !bg-primary/20  text-base-content/90;
}

.multiselect--disabled {
  background: transparent;
}
.multiselect--disabled .multiselect__current,
.multiselect--disabled .multiselect__select {
  background: transparent;
}
</style>
